import {
  Activity,
  Banknote,
  ClipboardList,
  Folder,
  Gauge,
  Hand,
  List,
  Settings,
  Siren,
  SlidersHorizontal,
  Store,
  Target,
  Users,
  type LucideIcon,
} from "lucide-react"

/**
 * Левое меню повторяет консоль SEON пункт в пункт (`reference/screens/`) —
 * порядок, названия и разрыв перед `Settings`. Названия не переводятся и не
 * сокращаются: аналитик ходит между настоящим SEON и этой консолью, и
 * несовпадение подписей заставило бы его каждый раз перечитывать меню.
 *
 * Работают здесь Dashboard, Transactions, Scoring Engine, Lists и Settings (люди аккаунта).
 * Остальные пункты ведут на заглушку — так видно объём консоли и то, что именно
 * реализовано, а «мёртвый» пункт без перехода выглядел бы поломкой.
 */
export interface NavItem {
  label: string
  path: string
  icon: LucideIcon
  /** Реализованный раздел. Остальные открывают заглушку. */
  live?: boolean
  /** Отбивка сверху — разрыв между блоком разделов и настройками, как в образце. */
  gapBefore?: boolean
}

export const NAV: NavItem[] = [
  { label: "Dashboard", path: "/", icon: Gauge, live: true },
  { label: "Transactions", path: "/transactions", icon: List, live: true },
  { label: "Customers", path: "/customers", icon: Users },
  { label: "Alerts", path: "/alerts", icon: Siren },
  { label: "Cases", path: "/cases", icon: Folder },
  { label: "Manual Lookup", path: "/manual-lookup", icon: Hand },
  { label: "AML", path: "/aml", icon: Banknote },
  { label: "Scoring Engine", path: "/scoring", icon: SlidersHorizontal, live: true },
  { label: "Lists", path: "/lists", icon: ClipboardList, live: true },
  { label: "Monitoring", path: "/monitoring", icon: Target },
  { label: "Channels", path: "/channels", icon: Store },
  { label: "Logs", path: "/logs", icon: Activity },
  { label: "Settings", path: "/settings", icon: Settings, live: true, gapBefore: true },
]

/**
 * Вкладки Scoring Engine.
 *
 * Четвёртой вкладки образца — `AI rule suggestions` — здесь нет намеренно: она
 * выведена из объёма задачи. Не «забыли» и не «потом»: за ней нет ни данных, ни
 * ручки, и пустая вкладка читалась бы как сломанная.
 *
 * `Replay` — вкладка не из образца, нашей консоли своя: за ней стоит живая
 * ручка `POST /api/scoring/replay`.
 *
 * `Local rules` — тоже своя: правила, которых у SEON нет вовсе (AMEND-13 §3:
 * правило чёрного списка). Отдельной вкладкой, а не строкой среди `custom`:
 * рядом с выгрузкой SEON наше правило читалось бы как её часть.
 *
 * `Живые: мы и SEON` (AMEND-59 шаг 2) — тоже своя, сразу за `Replay`: это та же
 * сверка «наш движок против SEON», только по живым решениям двойника и
 * вебхукам, а не по прогону выгрузки. В Transactions её не ставим — там
 * строки, а здесь сравнение правил и баллов. Подпись по-русски, как у
 * «Журнала действий»: экрана с таким названием у SEON нет.
 */
export const SCORING_TABS = [
  { label: "Dashboard", path: "/scoring" },
  { label: "Default rules", path: "/scoring/default" },
  { label: "Custom rules", path: "/scoring/custom" },
  { label: "Local rules", path: "/scoring/local" },
  { label: "Replay", path: "/scoring/replay" },
  { label: "Живые: мы и SEON", path: "/scoring/live" },
] as const

/**
 * Вкладки Lists (AMEND-12 §4, §7).
 *
 * Живая одна — Blacklist: за ней таблица `list_entries` и пять ручек. Whitelist
 * и Custom lists стоят рядом заглушками, и это не «потом»: в выгрузке SEON их
 * нет ни строкой, а нарисовать им содержимое значило бы выдумать список, по
 * которому кого-то пропускают.
 */
export const LISTS_TABS = [
  { label: "Blacklist", path: "/lists", live: true },
  { label: "Whitelist", path: "/lists/whitelist", live: false },
  { label: "Custom lists", path: "/lists/custom", live: false },
] as const

/**
 * Вкладки Transactions (AMEND-16 §3 шаг 3).
 *
 * Обе наши: в консоли SEON заливки нет вовсе — выгрузку там делает сам сервис.
 * Отдельным пунктом меню `Import` не стал: меню повторяет образец пункт в пункт,
 * и чужая строка в нём читалась бы как раздел SEON, которого человек у себя не
 * найдёт. Внутри Transactions она стоит по делу — это тот же раздел, вид сбоку:
 * на одной вкладке строки, на другой то, откуда они берутся.
 *
 * Статический `import` побеждает `/transactions/:id` без оговорок: React Router
 * ранжирует сегменты, а не порядок объявления, — карточка с идентификатором
 * `import` невозможна (номер числовой), так что перехвата тут нет.
 */
export const TRANSACTIONS_TABS = [
  { label: "List", path: "/transactions", live: true },
  { label: "Import", path: "/transactions/import", live: true },
] as const

/**
 * Вкладки Settings (AMEND-35 шаг 2).
 *
 * Журнал — вкладка внутри Settings, а не пункт меню: меню повторяет SEON пункт
 * в пункт (см. `NAV`). Подпись журнала — по-русски, как требует бриф и как
 * подписан сам экран; соседняя вкладка остаётся в языке консоли.
 *
 * `adminOnly` — вкладку видит только админ аккаунта. Это подсказка, а не
 * заслон: ручка сама отвечает 403 всем, кто не админ, и экран это объясняет.
 *
 * `Timeouts` (AMEND-97 шаг 2) — таймауты и бюджеты решения. Подпись — как в
 * брифе; экран внутри по-русски, как у журнала.
 */
export const SETTINGS_TABS = [
  { label: "Account & people", path: "/settings", live: true, adminOnly: false },
  { label: "Журнал действий", path: "/settings/audit", live: true, adminOnly: true },
  { label: "Timeouts", path: "/settings/timeouts", live: true, adminOnly: true },
] as const

/** Хлебные крошки в шапке: раздел меню, а у вкладок — ещё и вкладка. */
export function crumbsFor(pathname: string): string[] {
  const tab = SCORING_TABS.find((item) => item.path === pathname)
  if (tab) return ["Scoring Engine", tab.label]

  // Только вложенные вкладки: у `/settings` хватает крошки раздела.
  const setting = SETTINGS_TABS.find((item) => item.path !== "/settings" && item.path === pathname)
  if (setting) return ["Settings", setting.label]

  // Раньше карточки: `/transactions/import` подходит под её шаблон и без этой
  // строки подписался бы «Card».
  if (pathname === "/transactions/import") return ["Transactions", "Import"]

  const list = LISTS_TABS.find((item) => item.path === pathname)
  if (list) return ["Lists", list.label]

  // Карточка правила вкладки не имеет — её открывают из таблицы. Без своей
  // крошки шапка показывала бы на ней только «Scoring Engine», и по крошкам
  // было бы не понять, что именно открыто.
  if (/^\/scoring\/rules\/[^/]+$/.test(pathname)) return ["Scoring Engine", "Rule"]

  // Карточка транзакции открывается из списка и своей вкладки не имеет: без
  // отдельной крошки шапка показывала бы на ней только «Transactions».
  if (/^\/transactions\/[^/]+$/.test(pathname)) return ["Transactions", "Card"]

  const section = [...NAV]
    .filter((item) => item.path !== "/")
    .find((item) => pathname === item.path || pathname.startsWith(`${item.path}/`))
  if (section) return [section.label]

  return pathname === "/" ? ["Dashboard"] : ["Not found"]
}
